import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LeadPerformance from './pages/LeadPerformance';
import DailyLog from './pages/DailyLog';
import { Leaderboard, MyStats, WeeklyReport } from './pages/StatsPages';
import Handoffs from './pages/Handoffs';
import WeeklyBestMessage from './pages/WeeklyBestMessage';
import Leads from './pages/Leads';
import FollowUps from './pages/FollowUps';
import LeadDetail from './pages/LeadDetail';
import UpworkReferrals from './pages/UpworkReferrals';
import UpworkReferralDetail from './pages/UpworkReferralDetail';
import { Guidelines, ReviewNotes, ProfileAssignments } from './pages/ResourcePages';
import { Gate2, Playbook, UserManagement, SettingsPage, Notifications, CostDashboard, MonthlyReport } from './pages/AdminPages';

// Where to send a logged-in user when they hit a route they're not
// allowed on (or land at /login while already authenticated).
const homeForRole = (role) =>
  ['admin', 'team_lead', 'business_manager', 'viewer'].includes(role)
    ? '/dashboard'
    : '/daily-log';

const ProtectedRoute = ({ children, allowedRoles, allowIf }) => {
  const auth = useAuth();
  const { user, loading } = auth;
  if (loading) return <div className="empty-state"><p>Loading...</p></div>;
  if (!user) return <Navigate to="/login" />;
  // A route can be guarded by role allowlist, by a custom predicate that
  // reads derived auth flags (e.g. canAccessUpwork), or by either passing.
  // If neither is provided, access falls back to any logged-in user.
  const roleOk = !allowedRoles || allowedRoles.includes(user.role);
  const flagOk = allowIf ? allowIf(auth) : false;
  if (!roleOk && !flagOk) return <Navigate to={homeForRole(user.role)} />;
  return <Layout>{children}</Layout>;
};

const App = () => {
  const { user, loading } = useAuth();

  if (loading) return <div className="empty-state" style={{ marginTop: 100 }}><p>Loading...</p></div>;

  return (
    <>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={user ? <Navigate to={homeForRole(user.role)} /> : <Login />} />

        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'business_manager', 'viewer']}>
            <Dashboard />
          </ProtectedRoute>
        } />

        <Route path="/lead-performance" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'viewer']}>
            <LeadPerformance />
          </ProtectedRoute>
        } />

        <Route path="/weekly-report" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'viewer']}>
            <WeeklyReport />
          </ProtectedRoute>
        } />

        <Route path="/my-stats" element={
          <ProtectedRoute><MyStats /></ProtectedRoute>
        } />

        <Route path="/daily-log" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'bde']}>
            <DailyLog />
          </ProtectedRoute>
        } />

        <Route path="/leaderboard" element={
          <ProtectedRoute><Leaderboard /></ProtectedRoute>
        } />

        <Route path="/handoffs" element={
          <ProtectedRoute><Handoffs /></ProtectedRoute>
        } />

        <Route path="/leads" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'bde', 'viewer']}>
            <Leads />
          </ProtectedRoute>
        } />
        <Route path="/leads/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'bde', 'viewer']}>
            <LeadDetail />
          </ProtectedRoute>
        } />

        <Route path="/upwork-referrals" element={
          <ProtectedRoute allowIf={(a) => a.canAccessUpwork}>
            <UpworkReferrals />
          </ProtectedRoute>
        } />
        <Route path="/upwork-referrals/:id" element={
          <ProtectedRoute allowIf={(a) => a.canAccessUpwork}>
            <UpworkReferralDetail />
          </ProtectedRoute>
        } />

        <Route path="/follow-ups" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'bde', 'viewer']}>
            <FollowUps />
          </ProtectedRoute>
        } />

        <Route path="/best-messages" element={
          <ProtectedRoute allowedRoles={['admin', 'team_lead', 'bde', 'viewer']}>
            <WeeklyBestMessage />
          </ProtectedRoute>
        } />

        <Route path="/guidelines" element={
          <ProtectedRoute><Guidelines /></ProtectedRoute>
        } />

        <Route path="/review-notes" element={
          <ProtectedRoute><ReviewNotes /></ProtectedRoute>
        } />

        <Route path="/profiles" element={
          <ProtectedRoute><ProfileAssignments /></ProtectedRoute>
        } />

        <Route path="/gate2" element={
          <ProtectedRoute allowedRoles={['admin', 'business_manager', 'viewer']}>
            <Gate2 />
          </ProtectedRoute>
        } />

        <Route path="/playbook" element={
          <ProtectedRoute allowedRoles={['admin', 'business_manager', 'viewer']}>
            <Playbook />
          </ProtectedRoute>
        } />

        <Route path="/users" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserManagement />
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SettingsPage />
          </ProtectedRoute>
        } />

        <Route path="/admin/ai-cost" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CostDashboard />
          </ProtectedRoute>
        } />

        <Route path="/admin/monthly-report" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <MonthlyReport />
          </ProtectedRoute>
        } />

        <Route path="/notifications" element={
          <ProtectedRoute><Notifications /></ProtectedRoute>
        } />

        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </>
  );
};

export default App;
